import React from 'react';
import { missingLocationAndLoadsPageValid, projectConfigurationWarningConditionValid } from '../utils/validation';
import { missingPageWarning } from './components/missingPageWarning';
import { projectConfigurationWarning } from './components/projectConfigurationWarning';
import { renderInputs } from './renderInputs';
import { dispatch } from '__common/store';
import { SET_PROJECT_OPTION } from 'actions';
import velocityPressureExposureCoefficientKz from '../fields/velocityPressureExposureCoefficientKz';
import { calculateKe, calculateKz, get_numerical_coefficient, get_design_life_factor, getFrictionFactor, calculateFlatRoofSnowLoad } from '../utils/coefficients';
import groundElevationFactorKe from '../fields/groundElevationFactorKe';
import windDirectionalityFactorKd from '../fields/windDirectionalityFactorKd';
import { isASCE716or722, isASCE722 } from '__common/constants/buildingCodes';
import topographicalFactorKzt from '../fields/topographicalFactorKzt';
import designLifeFactorFc from '../fields/designLifeFactorFc';
import numericalCoefficient from '../fields/numericalCoefficient';
import { apiField as roofExposureApiField, RoofExposureOption } from '../fields/types/roofExposure';
import { apiField as soilClassApiField } from '../fields/soilClass';
import { apiField as thermalFactorApiField } from '../fields/thermalFactor';
import { apiField as frictionFactorApiField } from '../fields/frictionFactor';
import { apiField as loadSharingTypeApiField, loadSharingOptions } from '../fields/loadSharingType';
import { apiField as fullyAdheredApiField } from '../fields/fullyAdhered';
import { apiField as seismicIpApiField } from '../fields/seismicIp';
import { apiField as snowIsApiField } from '../fields/snowIs';
import { apiField as windIwApiField } from '../fields/windIw';
import { apiField as allowManualAttachmentsApiField } from '../fields/allowManualAttachments';
import { apiField as maxBlockCountField } from '../fields/maxBlockCountField';
import { apiField as maxBlockNorthCornerCountField } from '../fields/maxBlockNorthCornerCountField';
import { apiField as minBlockCountField } from '../fields/minBlockCountField';
import { apiField as interiorBayCapacityField } from '../fields/interiorBayCapacityField';

import ResetChangesButton from '__common/components/Drawer/components/ResetChangesButton';
import { isCanadianChangesProduct, isEcoFoot2Plus, isRM10Evolution, isRM5, isRMGridFlex, isRmGridflex10D, isRmGridflex5Rwdi } from '__common/constants/products';
import { MAX_BLOCK_COUNTS, interiorBayCapacity, isHalfBlockAllowed, minBlockCount, rm5MaxBlockCount, rmGridflex10MaxBlockNorthCornerCount, rmGridflex10MaxBlockCount, rmGridflex5dMaxBlockCount } from '../utils/constants';
import { AttachmentOptimization } from '../fields/attachmentOptimizationCriteria';
import { apiField as useDefaultMinAndMaxBlock } from '../fields/useDefaultMinAndMaxBlockCount';
import { apiField as useDefaultMaxBlockForNorthCorner } from '../fields/useDefaultMaxBlockForNorthCorner';
import { apiField as useDefaultFlatSlopeRoofSnowLoadApiField } from '../fields/useDefaultFlatSlopeRoofSnowLoad';
import { apiField as flatRoofSnowLoadApiField } from '../fields/flatRoofSnowLoad';
import { kpaToPsf, psfToKpa } from '__common/calculations/unitConversions';
import { isMetricUnit } from 'engineering/components/engineeringProjectDocuments/utils/unitTypes';

export const additionalUserInputsPage = (
  currentConfig: Function,
  productId: number,
  initData: projectEnvConfig,
  mapType: string,
  location: string,
  envFactorsLoading: boolean,
  area: string,
  getErrorMessageIfInvalid: (fieldName: string) => string | null,
  inputUnit: number,
  module_length
) => {
  const buildingCode = initData['building_code'];
  const config = currentConfig(buildingCode);
  const hasManualRoofSnowLoadInputs = isRM5(productId) || isRM10Evolution(productId) || isEcoFoot2Plus(productId) ||
    isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId);

  const resetInputData = () => {
    const { wind_exposure, building_height, elevation, mean_recurrence_interval, zipcode, roof_type, nbcc_roughness, snow_load, risk_category } = initData;
    dispatch(SET_PROJECT_OPTION(velocityPressureExposureCoefficientKz.apiField, calculateKz(wind_exposure, building_height, zipcode, '', inputUnit, nbcc_roughness, isCanadianChangesProduct(productId), buildingCode)));
    dispatch(SET_PROJECT_OPTION(groundElevationFactorKe.apiField, calculateKe(elevation, isASCE716or722(buildingCode), inputUnit, '', zipcode)));
    dispatch(SET_PROJECT_OPTION(windDirectionalityFactorKd.apiField, 0.85));
    dispatch(SET_PROJECT_OPTION(topographicalFactorKzt.apiField, 1));
    dispatch(SET_PROJECT_OPTION(designLifeFactorFc.apiField, get_design_life_factor(Number(mean_recurrence_interval))));
    dispatch(SET_PROJECT_OPTION(numericalCoefficient.apiField, get_numerical_coefficient(buildingCode, elevation, inputUnit, zipcode, '')));
    dispatch(SET_PROJECT_OPTION(roofExposureApiField, RoofExposureOption.partiallyExposed.value));
    dispatch(SET_PROJECT_OPTION(soilClassApiField, 4));
    dispatch(SET_PROJECT_OPTION(maxBlockCountField, MAX_BLOCK_COUNTS[productId]))
    dispatch(SET_PROJECT_OPTION(minBlockCountField, minBlockCount(initData.attachment_optimization_criteria === AttachmentOptimization.FULLY_ATTACHED, isHalfBlockAllowed(initData.is_half_block_allowed), productId)))
    dispatch(SET_PROJECT_OPTION(interiorBayCapacityField, interiorBayCapacity(productId, isHalfBlockAllowed(initData.is_half_block_allowed))))
    if (hasManualRoofSnowLoadInputs) {
      dispatch(SET_PROJECT_OPTION(useDefaultFlatSlopeRoofSnowLoadApiField, 1));
      const defaultThermalFactor = isASCE722(buildingCode) ? 1.2 : 1;
      dispatch(SET_PROJECT_OPTION(thermalFactorApiField, defaultThermalFactor));
      const pg = isMetricUnit(inputUnit) ? kpaToPsf(snow_load) : snow_load;
      const calculatedPf = calculateFlatRoofSnowLoad(
        pg, defaultThermalFactor, wind_exposure, RoofExposureOption.partiallyExposed.value, risk_category, buildingCode,
      );
      const pf = isMetricUnit(inputUnit) ? Number(psfToKpa(calculatedPf).toFixed(2)) : calculatedPf;
      dispatch(SET_PROJECT_OPTION(flatRoofSnowLoadApiField, pf));
    }
    if (isRmGridflex10D(productId) || isRMGridFlex(productId)  || isRmGridflex5Rwdi(productId)) {
      dispatch(SET_PROJECT_OPTION(thermalFactorApiField, isASCE722(buildingCode) ? 1.2 : 1));
      dispatch(SET_PROJECT_OPTION(frictionFactorApiField, getFrictionFactor(Number(roof_type), productId)));
      dispatch(SET_PROJECT_OPTION(loadSharingTypeApiField, loadSharingOptions.default.value));
      dispatch(SET_PROJECT_OPTION(fullyAdheredApiField, 1));
      dispatch(SET_PROJECT_OPTION(windIwApiField, 1));
      dispatch(SET_PROJECT_OPTION(seismicIpApiField, 1));
      dispatch(SET_PROJECT_OPTION(snowIsApiField, 1));
      dispatch(SET_PROJECT_OPTION(allowManualAttachmentsApiField, 0));
    }
    if (isRM5(productId) || isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId)) {
      dispatch(SET_PROJECT_OPTION(useDefaultMinAndMaxBlock, 1))
      if ( isRmGridflex10D(productId)){
        dispatch(SET_PROJECT_OPTION(maxBlockCountField, rmGridflex10MaxBlockCount(isHalfBlockAllowed(initData.is_half_block_allowed), initData.blocks_orientation)))
      }else if(isRmGridflex5Rwdi(productId)){
        dispatch(SET_PROJECT_OPTION(maxBlockCountField, rmGridflex5dMaxBlockCount(isHalfBlockAllowed(initData.is_half_block_allowed), module_length)))
      }else{
        dispatch(SET_PROJECT_OPTION(maxBlockCountField, rm5MaxBlockCount(isHalfBlockAllowed(initData.is_half_block_allowed))))
      }
    }
    if (isRmGridflex10D(productId)){
        dispatch(SET_PROJECT_OPTION(maxBlockNorthCornerCountField,  rmGridflex10MaxBlockNorthCornerCount(isHalfBlockAllowed(initData.is_half_block_allowed), module_length)))
        dispatch(SET_PROJECT_OPTION(useDefaultMaxBlockForNorthCorner, 1))
    }
  }
  return {
    content: (
      <>
        <div className="drawer-section-title">Additional User Inputs</div>
        {missingLocationAndLoadsPageValid(productId)
          ? null
          : missingPageWarning()}
        <div className="drawer-fields">
          <div className="drawer-section-subtitle">
            Wind Pressure Inputs
          </div>
          <div>{renderInputs(config.WindPressureInputs, mapType, location, initData, productId, getErrorMessageIfInvalid)}</div>
        </div>
        <br />
        <div className="drawer-fields">
          <div className="drawer-section-subtitle" id="additional-user-input-subtitle">
            Snow Load Inputs
          </div>
          {config.useDefaultFlatSlopeRoofSnowLoad && hasManualRoofSnowLoadInputs && <div className="drawer-fields additional-user-inputs-checkbox">
            <div>{renderInputs(config.useDefaultFlatSlopeRoofSnowLoad, mapType, location, initData, productId, getErrorMessageIfInvalid)}</div>
          </div>}
          <div>{renderInputs(config.SnowLoadInputs, mapType, location, initData, productId, getErrorMessageIfInvalid)}</div>
        </div>
        {
          isRmGridflex10D(productId) || isRMGridFlex(productId)  || isRmGridflex5Rwdi(productId) ?
            <>
              <br />
              <div className="drawer-fields">
                <div className="drawer-section-subtitle" id="additional-user-input-subtitle">
                  Load Sharing
                </div>
                <div>{renderInputs(config.LoadSharing, mapType, location, initData, productId, getErrorMessageIfInvalid)}</div>
              </div>
            </>
            : <></>
        }
        {config.SeismicInputs && <>
          <br />
          <div className="drawer-fields">
            <div className="drawer-section-subtitle" id="additional-user-input-subtitle">
              Seismic Inputs
            </div>
            <div>{renderInputs(config.SeismicInputs, mapType, location, initData, productId, getErrorMessageIfInvalid)}</div>
          </div>
        </>}
        {
          (isRM10Evolution(productId) || isEcoFoot2Plus(productId) || isRM5(productId)  || isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId)) ?
            <>
              <br />
              <div className="drawer-fields">
                <div className="drawer-section-subtitle" id="additional-user-input-subtitle">
                  Max And Min Block Count
                </div>
                {config.useDefaultMinAndMaxBlock && (isRM5(productId) || isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId)) && <div className="drawer-fields additional-user-inputs-checkbox">
                  <div>{renderInputs(config.useDefaultMinAndMaxBlock, mapType, location, initData, productId, getErrorMessageIfInvalid)}</div>
                </div>}
                {config.MaxAndMinBlockCount?.fields?.length > 0 && <div>{renderInputs(config.MaxAndMinBlockCount, mapType, location, initData, productId, getErrorMessageIfInvalid)}</div>}
              </div>
            </>
            : <></>
        }
        {
          (isRmGridflex10D(productId)) ?
            <>
              <br />
              <div className="drawer-fields">
                <div className="drawer-section-subtitle" id="additional-user-input-subtitle">
                  Max Block for North Corner
                </div>
                {config.useDefaultMaxBlockForNorthCorner &&  isRmGridflex10D(productId) &&  <div className="drawer-fields additional-user-inputs-checkbox">
                  <div>{renderInputs(config.useDefaultMaxBlockForNorthCorner, mapType, location, initData, productId, getErrorMessageIfInvalid)}</div>
                </div>}
                {config.MaxBlockNorthCornerCount?.fields?.length > 0 && <div>{renderInputs(config.MaxBlockNorthCornerCount, mapType, location, initData, productId, getErrorMessageIfInvalid)}</div>}
              </div>
            </>
            : <></>
        }
        <div className={`additional-user-inputs-reset${Boolean(config.useDefaultMinAndMaxBlock && initData[useDefaultMinAndMaxBlock] && (isRM5(productId) || isRmGridflex5Rwdi(productId))) ? ' additional-user-inputs-reset--after-checkbox' : ''}`}>
          <ResetChangesButton label='RESET' onClick={resetInputData} />
        </div>
        {projectConfigurationWarningConditionValid(productId) ? projectConfigurationWarning() : null}
      </>
    ),
  };
};
